import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, defer, forkJoin, from, of, throwError } from 'rxjs';
import {
  catchError,
  defaultIfEmpty,
  last,
  map,
  mergeMap,
  switchMap,
  take,
  toArray
} from 'rxjs/operators';

import { isNil } from '@gfe/core/utils/shared-utils';

import {
  EntityModificationAction,
  ModificationCascade,
  ModificationCascadeDependents,
  RegistrationRepairScope
} from '@core/models/entities/entity-visualization-interface';
import { PlanFilter } from '@core/models/entity/entity-selector-request.model';
import { EntityModificationService } from '@core/services/entity-modification/entity-modification.service';
import { SelectorsService } from '@core/services/selectors/selectors.service';
import { GenericEntityRegistryService } from '@core/services/user-models-registry/generic-entity-registry.service';
import { entityModified } from '@core/store/data-visualization/data-visualization.actions';

export type CascadeResult =
  | {
      status: 'applied' | 'partial';
      /** The payload written and the resources it moved, for a follow-up repair pass. */
      fields?: Record<string, unknown>;
      repairScope?: RegistrationRepairScope;
    }
  | { status: 'aborted'; reasonKey: string; params?: Record<string, unknown> };

/** Fetch scope of one cascade, snapshotted so a later `setContext` cannot redirect it. */
interface CascadeContext {
  dataSpaceId: number | undefined;
  planFilter: PlanFilter | undefined;
}

/**
 * Dependent writes are independent PUTs and a route can serve dozens of jobs; firing them
 * all at once would burst that many concurrent requests at the backend.
 */
const DEPENDENT_WRITE_CONCURRENCY = 4;

/**
 * Orchestrates a two-stage modification: load the selected entity, write the primary
 * payload a pure `derive` produced, then write any dependent entities' patches.
 *
 * The primary write happens first on purpose. Dependent writes here only carry display
 * KPIs, so a dependent failure leaves a correct primary entity with stale KPIs — the state
 * the app is in without a cascade at all. The reverse order would leave dependents
 * asserting a value the primary entity does not hold.
 */
@Injectable({ providedIn: 'root' })
export class ModificationCascadeService {
  private readonly selectorsService = inject(SelectorsService);
  private readonly entityModificationService = inject(EntityModificationService);
  private readonly registry = inject(GenericEntityRegistryService);
  private readonly store = inject(Store);

  private dataSpaceId: number | undefined;
  private planFilter: PlanFilter | undefined;

  /** Callers supply the fetch scope, mirroring how EntityModificationService resolves it. */
  setContext(dataSpaceId: number | undefined, planFilter: PlanFilter | undefined): void {
    this.dataSpaceId = dataSpaceId;
    this.planFilter = planFilter;
  }

  /**
   * Emits exactly one outcome, or errors. It never completes silently: a write that resolves
   * no transformer or no destination completes without emitting, and reporting that as a
   * success would tell the planner a change landed when nothing was written.
   */
  resolveAndApply$(
    action: EntityModificationAction,
    newValue: string
  ): Observable<CascadeResult> {
    const cascade = action.cascade;
    if (isNil(cascade)) {
      return throwError(
        () => new Error(`Modification action "${action.labelKey}" declares no cascade`)
      );
    }

    const context: CascadeContext = {
      dataSpaceId: this.dataSpaceId,
      planFilter: this.planFilter
    };

    return this.loadSource$(cascade, newValue, context).pipe(
      take(1),
      switchMap((source) => {
        if (source.missing) {
          return of<CascadeResult>({
            status: 'aborted',
            reasonKey: cascade.sourceNotFoundReasonKey ?? 'common.error'
          });
        }

        const outcome = cascade.derive(source.value);
        if ('abortReasonKey' in outcome) {
          return of<CascadeResult>({
            status: 'aborted',
            reasonKey: outcome.abortReasonKey,
            params: outcome.params
          });
        }

        return this.entityModificationService
          .applyModification(action, {
            labelKey: action.labelKey,
            isCurrentValue: false,
            data: () => outcome.fields
          })
          .pipe(
            switchMap(() =>
              this.applyDependents$(cascade.dependents, source.value, context)
            ),
            map((result) =>
              result.status === 'aborted'
                ? result
                : { ...result, fields: outcome.fields, repairScope: outcome.repairScope }
            )
          );
      }),
      take(1),
      // `last` is what converts a stage that completed without emitting into an error.
      last()
    );
  }

  private loadSource$(
    cascade: ModificationCascade,
    newValue: string,
    context: CascadeContext
  ): Observable<{ missing: boolean; value: unknown }> {
    if (isNil(cascade.source)) {
      return of({ missing: false, value: newValue });
    }
    if (isNil(context.dataSpaceId)) {
      return of({ missing: true, value: undefined });
    }

    return this.selectorsService
      .selectEntityByUid$(
        cascade.source.entityType,
        cascade.source.entityTypeVersion,
        newValue,
        context.dataSpaceId,
        context.planFilter
      )
      .pipe(map((entity) => ({ missing: isNil(entity), value: entity?.raw })));
  }

  /**
   * The primary entity is already written by the time this runs, so no failure here may
   * surface as an error: it would tell the planner nothing changed when the primary entity
   * did. Expanding the versions, fetching the dependents and writing them all degrade to
   * `partial` — the primary write stands, the dependents' KPIs may be stale.
   */
  private applyDependents$(
    dependents: ModificationCascadeDependents | undefined,
    source: unknown,
    context: CascadeContext
  ): Observable<CascadeResult> {
    return defer(() => this.writeDependents$(dependents, source, context)).pipe(
      catchError(() => of<CascadeResult>({ status: 'partial' }))
    );
  }

  private writeDependents$(
    dependents: ModificationCascadeDependents | undefined,
    source: unknown,
    context: CascadeContext
  ): Observable<CascadeResult> {
    const dataSpaceId = context.dataSpaceId;
    if (isNil(dependents) || dependents.uids.length === 0) {
      return of<CascadeResult>({ status: 'applied' });
    }

    // Dependents were named, so from here on anything that stops the stage before a write
    // leaves their KPIs stale and must be reported as such — claiming `applied` would tell the
    // planner the cascade landed in full.
    if (isNil(dataSpaceId)) {
      return of<CascadeResult>({ status: 'partial' });
    }

    const prefix = dependents.entityTypePrefix.toUpperCase();
    const targets = this.registry
      .availableTypeVersionPairs()
      .filter(([entityType]) => entityType.toUpperCase().startsWith(prefix));

    if (targets.length === 0) {
      return of<CascadeResult>({ status: 'partial' });
    }

    // One fetch per (entity type, version) pair: `fetchAuxiliaryData$` keys its request by
    // entity type, so an entity type carrying several versions would otherwise be reduced
    // to a single version and the rest of its dependents would silently never be written.
    const fetches = targets.map(([entityType, versionName]) =>
      this.selectorsService
        .fetchAuxiliaryData$(
          {
            versionNameAndUidsPerType: { [entityType]: [versionName, dependents.uids] }
          },
          dataSpaceId,
          context.planFilter
        )
        .pipe(
          take(1),
          map((bundle) => ({
            entityType,
            versionName,
            items: bundle.entitiesArrayPerVersionName[versionName] ?? [],
            failed: false
          })),
          // One unreachable version must not discard the groups that did load: `forkJoin`
          // fails fast, so without this a single bad fetch would leave every dependent's KPI
          // stale rather than only that version's.
          catchError(() =>
            of({ entityType, versionName, items: [] as unknown[], failed: true })
          )
        )
    );

    return forkJoin(fetches).pipe(
      switchMap((groups) => {
        const anyFetchFailed = groups.some((group) => group.failed);
        const writes: Observable<boolean>[] = [];
        const writtenEntityTypes = new Set<string>();

        for (const { entityType, versionName, items } of groups) {
          for (const item of items) {
            const patch = dependents.derive(item, source);
            if (isNil(patch)) continue;

            writtenEntityTypes.add(entityType);
            writes.push(
              this.entityModificationService
                .saveEntityData$(entityType, versionName, () => patch)
                .pipe(
                  map(() => true),
                  // A write that completes without emitting wrote nothing, exactly like
                  // one that threw, and must not count towards a full success.
                  defaultIfEmpty(false),
                  catchError(() => of(false))
                )
            );
          }
        }

        if (writes.length === 0) {
          // Uids were requested, so the dependents exist somewhere. Finding none means the
          // fetch scope missed them — a plan filter that excludes them, or an entity type
          // the prefix does not reach — and their KPIs stay stale. Rows found but no patch
          // derived is the opposite case: every dependent already holds the right value.
          const fetched = groups.reduce((total, group) => total + group.items.length, 0);
          return of<CascadeResult>({
            status: fetched === 0 || anyFetchFailed ? 'partial' : 'applied'
          });
        }

        return from(writes).pipe(
          mergeMap((write) => write, DEPENDENT_WRITE_CONCURRENCY),
          toArray(),
          map((results) => {
            if (results.some(Boolean)) {
              // Open views on the dependents read stale values until told otherwise; one
              // refresh per type once the writes settled avoids a refetch storm.
              writtenEntityTypes.forEach((entityType) =>
                this.store.dispatch(entityModified({ entityType }))
              );
            }

            return results.every(Boolean) && !anyFetchFailed
              ? ({ status: 'applied' } as CascadeResult)
              : ({ status: 'partial' } as CascadeResult);
          })
        );
      })
    );
  }
}
